import { action, observable } from 'mobx';
import { api } from '../config';

// Components
import ExamStore from './ExamStore';
import PaymentStore from './PaymentStore';
import SessionStore from './SessionStore';
import StudentStore from './StudentStore';

// Dependencies
import moment from 'moment';
import LogRocket from 'logrocket';

// Helpers
import { formatQuery } from '../helpers/helpers';

var obx = observable({
  examSessionSid: null,
  timeZone: null,
  examDuration: null,
  examStartDate: null,
  examEndDate: null,
  externalReservedAppointmentExpires: null,
  externalReservedAppointmentId: null,
  appointmentDate: null,
  voucherCode: null,
  // Reset data
  resetData: action(function(){
    // Session data
    obx.appointmentDate = null;
    obx.examDuration = null;
    obx.examEndDate = null;
    obx.examSessionSid = null;
    obx.examStartDate = null;
    obx.externalReservedAppointmentExpires = null;
    obx.externalReservedAppointmentId = null;

    ExamStore.externalReservedAppointmentId = null;

    // Payment data
    obx.voucherCode = null;
    PaymentStore.paymentToken = {};

    // Availability data
    obx.onlineAvailability = [];
    obx.onlineDate = null;
    obx.onlineLocation = null;
    obx.onlineTimeGroups = null;
    obx.onlineTimeRange = '';
    // User data
    obx.timeZone = null;
  }),

  /**
   * Reset session-specific data after scheduling
   *
   * @function resetSessionData
   */
  resetSessionData: action (() => {
    obx.externalReservedAppointmentExpires = null;
    obx.externalReservedAppointmentId = null;
    obx.voucherCode = null;

    PaymentStore.paymentToken = {};
  }),

  // Get B Virtual Availability
  getBVAvailability: action(function(examSid, sessionSid) {
    SessionStore.startBackgroundLoading('getBVAvailability');

    let query = '';

    if(obx.onlineDate) {
      query = `?date=${obx.onlineDate}`
    }

    // Reset grouped times
    obx.onlineTimeGroups = null;

    api.get(`/installs/${SessionStore.userData.installSid}/courses/${SessionStore.userData.courseSid}/exams/${examSid}/enrollments/${SessionStore.userData.enrollmentSid}/availability/bvirtual${query}`)
      .then(response => {
        obx.onlineAvailability = response.data.availability
        obx.examSessionSid = sessionSid;
        obx.timeZone = SessionStore.userTimezone;
        obx.examDuration = ExamStore.examSession.examConfiguration.duration;
        obx.examStartDate = ExamStore.examSession.examConfiguration.examOpenDate;
        obx.examEndDate = ExamStore.examSession.examConfiguration.examCloseDate;

        // Group times
        obx.groupOnlineTimes(response.data.availability);

        SessionStore.finishBackgroundLoading('getBVAvailability');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get online availability
  onlineDate: null,
  onlineLocation: null,
  onlineAvailability: [],
  onlineTimeGroups: null,
  onlineTimeRange: '',
  getOnlineAvailability: action(function(examSid, sessionSid, query, isReschedule = false) {
    let queryString = '';

    SessionStore.startBackgroundLoading('getOnlineAvailability');

    if(query) {
      queryString = formatQuery(query);
    } else {
      if(obx.onlineLocation) {
        queryString += `?proctorLocationSids=${obx.onlineLocation}`
      }
    }

    if(obx.onlineDate) {
      queryString += `&startDate=${obx.onlineDate}`;
      queryString += `&endDate=${moment(obx.onlineDate).tz(SessionStore.userTimezone).endOf('day').toISOString()}`;
    }
    if (isReschedule) {
      queryString += '&reschedule=true';
    }

    // Reset grouped times
    obx.onlineTimeGroups = null;

    api.get(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}/availability${queryString}`)
      .then(response => {
        if(response.data.combined) {
          obx.onlineAvailability = response.data.combined.availability
        } else {
          obx.onlineAvailability = response.data[obx.onlineLocation].availability
        }

        obx.examSessionSid = sessionSid;
        obx.timeZone = SessionStore.userTimezone;
        obx.examDuration = ExamStore.examSession.examConfiguration.duration;
        obx.examStartDate = ExamStore.examSession.examConfiguration.examOpenDate;
        obx.examEndDate = ExamStore.examSession.examConfiguration.examCloseDate;

        // Make sure start date is named startDate
        obx.onlineAvailability.forEach(time => {
          if(!time.startDate && time.start) {
            time.startDate = time.start;
            delete time.start;
          }
        })

        // Group times
        obx.groupOnlineTimes(obx.onlineAvailability);

        SessionStore.finishBackgroundLoading('getOnlineAvailability');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Group online availability times
  groupOnlineTimes: action(function(times){
    times.forEach(time => {
      const date = moment(time.startDate).tz(SessionStore.userTimezone).format('MMDDYYYY');
      const hour = parseInt(moment(time.startDate).tz(SessionStore.userTimezone).format('H'), 10);

      if(!time.endDate) {
        time.endDate = moment(time.startDate).add(obx.examDuration, 'minutes').toISOString();
      }

      if(obx.onlineLocation) {
        time.proctorLocationSids = [obx.onlineLocation];
      }

      // Convert online times to object
      if(!obx.onlineTimeGroups) {
        obx.onlineTimeGroups = {}
      }

      // If an object doesn’t already exist for this date, create one
      if(!obx.onlineTimeGroups[date]) {
        obx.onlineTimeGroups[date] = {
          morning: [],
          afternoon: [],
          evening: []
        };
      }

      if(hour < 12) {
        obx.onlineTimeGroups[date].morning.push(time);
      } else if(hour < 18) {
        obx.onlineTimeGroups[date].afternoon.push(time);
      } else {
        obx.onlineTimeGroups[date].evening.push(time);
      }
    });
  }),
  // Check if a B Virtual reservation exists
  checkOnlineReservation: action(function(examSid, sessionSid, func, component){
    SessionStore.startLoading('Loading…', 'checkOnlineReservation');

    api.get(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}`)
      .then(response => {
        SessionStore.finishLoading('checkOnlineReservation');

        obx.examSessionSid = response.data.sid;

        ExamStore.examSession = response.data;

        // If a session is already reserved, check if the reservation is expired
        if(response.data.externalReservedAppointmentId) {
          if(
            moment(response.data.externalReservedAppointmentExpires).isAfter(
              SessionStore.currentTime
            )
          ) {
            // If it’s not expired, skip to the confirmation step
            obx.externalReservedAppointmentExpires = response.data.externalReservedAppointmentExpires;

            func();

            if(component) {
              component.setState({
                appointmentStartDate: response.data.appointmentDate
              })
            }
          } else {
            // Otherwise, expire the reservation
            const callback = () => {
              component.setStep(null, 8);
            }

            obx.resetData();
            obx.cancelReservation(examSid, sessionSid, callback);
          }
        } else {
          if(component) {
            component.setStep(null, 1);
          }
        }
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Cancel session
  cancellationMemo: null,
  cancelSession: action(function(examSid, sessionSid, schedulingModule, memo){
    SessionStore.startLoading('Loading…', 'CancelExamSession');

    const data = {
      type: schedulingModule,
      cancellationMemo: obx.cancellationMemo
    }

    api.delete(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}`, {data: data})
      .then(response => {
        SessionStore.finishLoading('CancelExamSession');
        StudentStore.getOrderedStudentExams();
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Cancel reservation
  cancelReservation: action(function(examSid, sessionSid, callback){
    SessionStore.startLoading('Loading…', 'CancelReservation');

    api.delete(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}/reserve`)
      .then(response => {
        SessionStore.finishLoading('CancelReservation');

        if(callback) {
          callback();
        }
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Reserve session slot
  createReservation: action(function(data, examSid, sessionSid, func){
    SessionStore.startLoading('Loading…', 'ReserveSession');

    api.post(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}/reserve`, data)
      .then(response => {
        SessionStore.finishLoading('ReserveSession');
        obx.externalReservedAppointmentId = response.data.externalReservedAppointmentId;
        obx.externalReservedAppointmentExpires = response.data.externalReservedAppointmentExpires;
        obx.appointmentDate = response.data.appointmentDate;
        func();
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Create a session appointment
  createAppointment: action(function(examSid, sessionSid, data, callback){
    SessionStore.startLoading('Loading…', 'CreateAppointment');

    data.cardToken = PaymentStore.paymentToken.id;

    if(obx.voucherCode) {
      data.voucherCode = obx.voucherCode;
    }

    if(data.sid && data.type === 'ad-hoc') {
      delete data.sid;
    }

    if(data.amount === null) {
      data.amount = 0;
    }

    api.post(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}`, data)
      .then(response => {
        ExamStore.examSession = response.data;

        if(callback) {
          callback(response);
        }

        obx.resetSessionData();

        SessionStore.finishLoading('CreateAppointment');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),

  // Check if able to schedule before a redirect
  verifyAbilityToScheduleHybrid: action(async function(examSid, sessionSid, appointmentStartDate) {
    SessionStore.startLoading('Loading…', 'VerifyAbilityToSchedule');
    const appointmentDate = new Date(appointmentStartDate).toISOString();

    const queryString = `?appointmentDateTime=${appointmentDate}`;

    const response = await api.get(
      `/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}/hybrid/abilityToSchedule${queryString}`);

    SessionStore.finishLoading('VerifyAbilityToSchedule');
    return response.data;
  }),
  // Reschedule appointment
  rescheduleAppointment: action(function(examSid, sessionSid, data, callback, refreshCallback){
    SessionStore.startLoading('Loading…', 'RescheduleAppointment');

    delete data.proctorLocationSid;

    if(data.locationLatitude) {
      data.locationLatitude = data.locationLatitude.toString();
    }

    if(data.locationLongitude) {
      data.locationLongitude = data.locationLongitude.toString();
    }

    api.put(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}`, data, data)
      .then(response => {
        SessionStore.finishLoading('RescheduleAppointment');
        StudentStore.getOrderedStudentExams(true);
        obx.externalReservedAppointmentId = null;
        obx.externalReservedAppointmentExpires = null;

        if(response.status === 205 && refreshCallback) {
          refreshCallback();
        } else if(callback) {
          callback();
        }
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Initiate retake
  retakeSession: action(function(examSid, sessionSid, redirectUrl, callback){
    SessionStore.startLoading('Loading…', 'RetakeSession');

    api.post(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}/retake`)
      .then(response => {
        SessionStore.finishLoading('RetakeSession');

        if(redirectUrl) {
          window.location.href = redirectUrl.replace(sessionSid, response.data.sid);
        }

        if(callback) {
          callback();
        }
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get scheduling token
  schedulingURL: null,
  getSchedulingToken: action(function(examSid, sessionSid, locationSid, appointmentDateTime) {
    SessionStore.startBackgroundLoading('GetSchedulingToken');

    let queryString = `?locationSid=${locationSid}`;

    if(appointmentDateTime) {
      queryString += `&appointmentDateTime=${appointmentDateTime}`
    }

    api.get(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}/schedulingToken${queryString}`)
      .then(response => {
        obx.schedulingURL = `${response.data.redirectUri}?token=${response.data.token}`;
        SessionStore.finishBackgroundLoading('GetSchedulingToken');
      })
      .catch(error => {
        SessionStore.throwError(error);
        LogRocket.captureException('Missing RegisterBlast campus key');
      })
  }),
  // Start online session
  startOnlineSession: action(function(examSid, sessionSid, callback, sessionWindow) {
    SessionStore.startBackgroundLoading('startOnlineSession');

    api.get(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}/start`)
      .then(response => {
        SessionStore.finishBackgroundLoading('startOnlineSession');

        if(callback) {
          callback(response.data.redirect);
        }
      })
      .catch(error => {
        SessionStore.throwError(error);

        if(sessionWindow) {
          sessionWindow.close();
        }
      })
  }),

  /**
   * uploadSessionPhoto
   * @param {string} examSid
   * @param {string} sessionSid
   * @param {object} data
   * @param {function} errorCallback
   */
  uploadSessionPhoto: action(function(examSid, sessionSid, data, errorCallback, responseCallback, photo) {
    SessionStore.startBackgroundLoading('uploadSessionPhoto');

    let formData = new FormData();

    formData.append('type', data.type);
    formData.append('photo', data.photo);

    console.log('===== Data =====');
    console.log(data);

    console.log('===== Form Data =====');
    console.log(formData);

    api.put(`/installs/${SessionStore.userData.installSid}/exams/${examSid}/sessions/${sessionSid}/photos`, formData)
      .then((response) => {
        // If 1st photo, just submit
        if (photo === 1) {
          SessionStore.finishBackgroundLoading('uploadSessionPhoto');
        }
        // If api call is successful, allow user to pass
        if (photo === 2 && responseCallback && response.status === 200) {
          SessionStore.finishBackgroundLoading('uploadSessionPhoto');
          responseCallback(true);
          //Otherwise, go back to start of ID verification
        } else if (photo === 2 && responseCallback && response.status !== 200) {
          responseCallback(false);
        }
      })
      .catch(error => {
        SessionStore.throwError(error);

        if(errorCallback) {
          errorCallback();
        }
      })
  })
});

export default obx;



// WEBPACK FOOTER //
// ./src/app/stores/ScheduleStore.js